import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useRef } from 'react';
import { getNotifications, getUnreadCount, markNotificationRead, markAllRead, testPush } from '../lib/api';
import { Bell, UserSwitch, Sparkle, DeviceMobile } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { getUser } from '../lib/session';
import { registerServiceWorker, requestNotificationPermission, subscribeToPush } from '../lib/notifications';
import { toast } from 'sonner';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushState, setPushState] = useState('unknown'); // granted | denied | default | unsupported | unknown
  const [enabling, setEnabling] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (typeof Notification === 'undefined') { setPushState('unsupported'); return; }
    setPushState(Notification.permission);
  }, [open]);

  const loadData = useCallback(async () => {
    try {
      const [notifs, count] = await Promise.all([getNotifications(), getUnreadCount()]);
      setNotifications(notifs.slice(0, 20));
      setUnread(count.count);
    } catch (e) {
      logError('Failed to load notifications:', e);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkRead = useCallback(async (id) => {
    try { await markNotificationRead(id); loadData(); } catch (e) { logError('Mark read failed:', e); }
  }, [loadData]);

  const handleMarkAllRead = useCallback(async () => {
    try { await markAllRead(); loadData(); } catch (e) { logError('Mark all read failed:', e); }
  }, [loadData]);

  const enablePhoneAlerts = useCallback(async () => {
    const u = getUser();
    if (!u) { toast.error('Sign in first'); return; }
    setEnabling(true);
    try {
      await registerServiceWorker();
      const perm = await requestNotificationPermission();
      setPushState(perm);
      if (perm !== 'granted') {
        if (perm === 'denied') toast.error('Notifications blocked — enable in your browser settings');
        else toast.message('Permission dismissed');
        return;
      }
      const sub = await subscribeToPush(u.id);
      if (!sub) { toast.error('Could not subscribe this device'); return; }
      try { await testPush(u.id); } catch (e) { /* non-fatal */ }
      toast.success('Phone alerts are on — check your home screen');
    } finally { setEnabling(false); }
  }, []);

  const iconForType = (type) => {
    if (type === 'task_assigned') return <UserSwitch size={14} weight="duotone" className="text-[color:var(--mr-blue)]" />;
    return <Sparkle size={14} weight="duotone" className="text-[color:var(--mr-warn)]" />;
  };

  const isStandalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone);
  const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);
  const canPrompt = pushState !== 'granted' && pushState !== 'unsupported' && pushState !== 'denied';
  const needsInstallFirst = isIOS && !isStandalone;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        data-testid="notification-bell">
        <Bell size={17} weight={unread > 0 ? 'fill' : 'regular'} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[color:var(--mr-red)] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 max-w-[calc(100vw-24px)] bg-card border border-border rounded-lg shadow-lg z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-xs font-semibold text-foreground">Notifications</span>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-[10px] h-6 text-muted-foreground" data-testid="mark-all-read">
                Mark all read
              </Button>
            )}
          </div>

          {/* Phone alerts banner */}
          <div className="px-3 py-2.5 border-b border-border chip-info" data-testid="phone-alerts-row">
            <div className="flex items-start gap-2">
              <DeviceMobile size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {pushState === 'granted' ? (
                  <p className="text-[11px] font-semibold">Phone alerts are ON for this device</p>
                ) : pushState === 'denied' ? (
                  <p className="text-[11px] font-semibold">Blocked — enable notifications in your phone's browser settings</p>
                ) : pushState === 'unsupported' ? (
                  <p className="text-[11px] font-semibold">This browser doesn't support push alerts</p>
                ) : needsInstallFirst ? (
                  <>
                    <p className="text-[11px] font-bold">Install Huddle to get phone alerts</p>
                    <p className="text-[10px] mt-0.5 opacity-80">On iPhone: tap Share → "Add to Home Screen", then open Huddle from your home screen and tap "Enable" here.</p>
                  </>
                ) : (
                  <>
                    <p className="text-[11px] font-bold">Get alerts on your phone</p>
                    <p className="text-[10px] mt-0.5 opacity-80">Frustrated clients · Escalations assigned to you · Overdue commitments · Carry-forward from your huddle</p>
                  </>
                )}
                {canPrompt && !needsInstallFirst && (
                  <Button size="sm" onClick={enablePhoneAlerts} disabled={enabling}
                    className="mt-1.5 h-7 text-[11px] mr-btn-primary px-3" data-testid="enable-phone-alerts-btn">
                    {enabling ? 'Enabling…' : 'Enable phone alerts'}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="max-h-[300px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground">No notifications</div>
            ) : notifications.map(n => (
              <button key={n.id} onClick={() => handleMarkRead(n.id)}
                className={`w-full text-left px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/50 transition-colors ${!n.read ? 'bg-primary/5' : ''}`}
                data-testid={`notification-${n.id}`}>
                <div className="flex items-start gap-2">
                  {iconForType(n.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground leading-snug">{n.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {n.from_member && <span className="text-[10px] text-muted-foreground">from {n.from_member}</span>}
                      <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[color:var(--mr-blue)] flex-shrink-0 mt-1" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
